import { v4 as uuid } from 'uuid';
import { fromJS } from 'immutable';
import {
    takeEvery,
    put,
    select,
    call,
    takeLatest
} from 'redux-saga/effects';
import {
    SET_CAPI_SESSION,
    RETRIEVED_GET_CART_SUCCESS,
    GET_CART_FAILED,
    PROCESS_GET_CART_DATA
} from 'capi/redux';
import { requestPromoForCode } from 'capi';

import { setCartStatus, CART_STATUS } from '../cart/cartModule';
import {
    SAVE_NEW_ITEM_TO_CART,
    addItemInCart,
    setGrandTotal,
    RUN_AMOUNT_CALCULATIONS,
    E_CARD_TYPE,
    PHYSICAL_CARD_TYPE,
    handleAddBuyNowItem,
    setConsumerFeeDetails,
    flushProductDetails,
    setOrderNumber,
    setCartResumeDone
} from './productCartModule';
import {
    getGrandTotalCostWithFees,
    getPaymentEGiftCards,
    getPaymentPhysicalGiftCards
} from './productCartSelectors';
import {
    setPromo,
    setPromoDiscountAmount,
    SET_PROMO_REWARDS,
    setShippingCostData
} from '../promo/promoModule';
import { logError, reportPageLoadError } from '../utils/errorUtils';
import { getIsOldUiPreferred } from '../app/bootstrap';
import { calculateUyoFeeTotals, feeTypes, getFeeAmountByTypeAndCost } from '../fees/consumerFeeUtils';
import { getConsumerFees } from '../fees/consumerFeeSelectors';
import { changeStorefrontFlow, STOREFRONT_FLOW } from '../routing/routingModule';
import { ABANDONED_CART_STATUS_COMPLETED, CARD_TYPES } from '../constants/constants';
import { getFaceplateByCode } from '../landingPage/landingPageSelectors';
import { cartResponseDeserializer } from '../cart/cartResponseSerializer';
import { loadingAction } from '../loader/loaderModule';
import {
    selectedShippingCart,
    setSelectedShippingToken
} from '../shipping/shippingModule';

export function* saveNewItemToCart({ newItem }) {
    yield put(setCartStatus(CART_STATUS.PROCESSING));

    
    const {
        item,
        items,
        type,
        flow,
        ...rest
    } = newItem;
    
    const skipCart = getIsOldUiPreferred();

    
    if (![E_CARD_TYPE, PHYSICAL_CARD_TYPE].includes(newItem.type)) {
        
        logError('Invalid item type provided in saveNewItemToCart action payload', { type: newItem.type });
        yield put(setCartStatus(CART_STATUS.READY)); 
        reportPageLoadError('Invalid item type provided in saveNewItemToCart action payload',
            'Invalid item type provided in saveNewItemToCart action payload', {
                type: newItem.type
            });
        return;
    }

    let itemsToAdd = [];
    if (item) {
        
        
        item.id = uuid();
        
        item.quantity = item.quantity || 1;
        itemsToAdd = [{ ...item }];
    } else if (items) {
        
        
        itemsToAdd = (items)
            .filter(value => value) 
            .map(value => ({
                ...value,
                id: uuid(),
                quantity: value.quantity || 1
            }));
    }

    if (itemsToAdd.length === 0) {
        
        logError('No valid items found in saveNewItemToCart action payload');
        yield put(setCartStatus(CART_STATUS.READY));
        reportPageLoadError(
            'No valid items found in saveNewItemToCart action payload',
            'No valid items found in saveNewItemToCart action payload'
        );
        return;
    }

    const addToCartItem = fromJS({ [type]: itemsToAdd, ...rest });

    
    yield put(addItemInCart(addToCartItem, skipCart));

    if (flow === STOREFRONT_FLOW[1]) {
        
        
        yield put(handleAddBuyNowItem(addToCartItem));
        yield put(changeStorefrontFlow(flow));
    } else {
        yield put(changeStorefrontFlow());
    }

    
    

    
    yield put(setCartStatus(CART_STATUS.READY));
    
}


export function* runAmountCalculations() {
    const eGiftCards = yield select(getPaymentEGiftCards);
    
    const physicalCards = yield select(getPaymentPhysicalGiftCards);
    const allCards = [...eGiftCards, ...physicalCards];
    const consumerFees = yield select(getConsumerFees);
    let activationFeeTotal = 0;
    let activationFeeCount = 0;
    if (allCards.length > 0) {
        allCards
            .filter(item => item && !item.get('removed'))
            .forEach((item) => {
                const isPhysical = physicalCards.some(
                    physical => physical && physical.get('id') === item.get('id')
                );
                const feeType = isPhysical
                    ? feeTypes.PLASTIC_ACTIVATION
                    : feeTypes.DIGITAL_ACTIVATION;
                const amount = Number(item.get('cardValue'));
                const activationFeeAmount = getFeeAmountByTypeAndCost(consumerFees.toJS(), feeType, amount);
                const quantity = item.get('quantity');

                if (activationFeeAmount) {
                    activationFeeCount += quantity;
                    activationFeeTotal += (quantity * activationFeeAmount);
                }
            });
    }
    
    const { uyoFeeTotal, uyoFeeCount } = calculateUyoFeeTotals(physicalCards, consumerFees.toJS());

    
    yield put(setConsumerFeeDetails(activationFeeTotal, activationFeeCount, uyoFeeTotal, uyoFeeCount));

    
    const grandTotalWithFees = yield select(getGrandTotalCostWithFees);
    
    yield put(setGrandTotal(parseFloat(grandTotalWithFees.toFixed(2))));
}

export function* processGetCartSuccess(action) {
    try {
        const responseData = action.data;

        if (responseData?.request_num) {
            yield put({
                type: SET_CAPI_SESSION,
                id: responseData.request_num
            });
        }

        const cartStatus = responseData?.status;
        const isCompletedCart = cartStatus === ABANDONED_CART_STATUS_COMPLETED;

        if (isCompletedCart) {
            
            
            yield put(setCartResumeDone(false));
            yield put(loadingAction({ isLoading: false }));
            return;
        }

        const getFaceplateByCodeFn = yield select(getFaceplateByCode);

        const cartItems = cartResponseDeserializer(responseData, getFaceplateByCodeFn);

        if (cartItems && cartItems.length > 0) {
            yield put(flushProductDetails());

            const immutableItems = fromJS(cartItems);

            const eGiftCards = immutableItems.filter(
                item => item.get('itemCardType') === CARD_TYPES.Digital
            );
            const physicalCards = immutableItems.filter(
                item => item.get('itemCardType') === CARD_TYPES.Physical
            );

            const currencyCode = responseData.currency_code || 'USD';
            const cartData = {
                currencyCode
            };

            if (eGiftCards.size > 0) {
                cartData[E_CARD_TYPE] = eGiftCards;
            }

            if (physicalCards.size > 0) {
                cartData[PHYSICAL_CARD_TYPE] = physicalCards;

                
                
                for (let i = 0; i < physicalCards.size; i++) {
                    const card = physicalCards.get(i);
                    const shippingAddress = card.get('shippingAddress');
                    const shippingAmount = card.get('shippingAmount');
                    const displayData = card.get('displayData');

                    if (shippingAddress && shippingAmount) {
                        const token = shippingAddress.get('token');
                        const displayNameFromData = displayData?.getIn(
                            ['shippingDisplayName', `${token}_displayText`]
                        );
                        const displayName = displayNameFromData
                            || responseData.shipping_group
                            || 'Standard Shipping';

                        const shippingMethod = fromJS({
                            token,
                            cost: parseFloat(shippingAmount),
                            displayName,
                            currencyCode,
                            carrier: 'standard',
                            methodName: 'standard'
                        });

                        yield put(selectedShippingCart(shippingMethod));
                    }
                }
            }

            yield put(addItemInCart(fromJS(cartData)));

            if (responseData.order_number) {
                yield put(setOrderNumber(responseData.order_number));
            }

            const totalAmount = parseFloat(responseData.total_amount || 0);
            const orderFeeAmount = parseFloat(responseData.order_fee_amount || 0);

            
            let activationFeeCount = 0;
            cartItems.forEach((item) => {
                const quantity = item.quantity || 1;
                
                const hasDigitalFee = item.digitalActivationFeeAmount
                    && parseFloat(item.digitalActivationFeeAmount) > 0;
                const hasPhysicalFee = item.plasticActivationFeeAmount
                    && parseFloat(item.plasticActivationFeeAmount) > 0;
                if (hasDigitalFee || hasPhysicalFee) {
                    activationFeeCount += quantity;
                }
            });

            
            
            yield put(setConsumerFeeDetails(orderFeeAmount, activationFeeCount));

            yield put(setGrandTotal(totalAmount));

            
            const promoCode = responseData?.checkout_request_data?.promoCode;
            const totalDiscountRewardAmount = parseFloat(
                responseData?.checkout_request_data?.totalDiscountRewardAmount || 0
            );

            
            const shippingPromoData = responseData?.checkout_request_data
                ?.shippingPromoData;
            if (shippingPromoData && Object.keys(shippingPromoData).length > 0) {
                
                const shippingCostDataObj = {};
                Object.keys(shippingPromoData).forEach((token) => {
                    const updatedCost = parseFloat(
                        shippingPromoData[token].updatedCost || 0
                    );
                    const shippingGroups = responseData?.checkout_request_data
                        ?.shippingGroups || [];
                    const matchingGroup = shippingGroups.find(
                        group => group.shippingMethodToken === token
                    );
                    const actualCost = matchingGroup
                        ? parseFloat(matchingGroup.shippingAmount || 0) : 0;

                    shippingCostDataObj[token] = {
                        actualCost,
                        updatedCost
                    };
                });

                yield put(setShippingCostData(shippingCostDataObj));

                
                
                const firstShippingToken = Object.keys(shippingPromoData)[0];
                const firstShippingData = shippingPromoData[firstShippingToken];
                if (firstShippingToken && firstShippingData) {
                    const shippingMethod = fromJS({
                        token: firstShippingToken,
                        cost: parseFloat(firstShippingData.updatedCost || 0),
                        displayName: 'Standard Shipping',
                        currencyCode
                    });
                    yield put(setSelectedShippingToken(shippingMethod));
                }

                
                const rewards = {
                    totalDiscountRewardAmount: Math.abs(
                        totalDiscountRewardAmount
                    ),
                    promotionalShippingData: shippingPromoData
                };

                yield put({
                    type: SET_PROMO_REWARDS,
                    rewards
                });
            } else if (totalDiscountRewardAmount !== 0) {
                
                yield put(setPromoDiscountAmount(
                    Math.abs(totalDiscountRewardAmount)
                ));
            }

            if (promoCode) {
                try {
                    const locale = responseData?.checkout_request_data
                        ?.languageCode || 'en-us';
                    const currency = responseData.currency_code || 'USD';
                    const promoData = yield call(
                        requestPromoForCode,
                        promoCode,
                        locale,
                        currency
                    );

                    if (promoData && promoData.code) {
                        yield put(setPromo(promoData));
                    }
                } catch (promoError) {
                    logError('Error fetching promo data for abandoned cart', promoError);
                    reportPageLoadError(
                        'Error fetching promo data for abandoned cart',
                        promoError.message || 'Unknown error',
                        { promoError }
                    );
                }
            }
        }

        const hasCartItems = cartItems && cartItems.length > 0;
        yield put(setCartResumeDone(hasCartItems));
        yield put(loadingAction({ isLoading: false }));
    } catch (error) {
        logError('Error processing abandoned cart data', error);
        reportPageLoadError(
            'Error processing abandoned cart data',
            error.message || 'Unknown error',
            { error }
        );
        yield put(setCartResumeDone(false));
        yield put(loadingAction({ isLoading: false }));
    }
}

export function* processGetCartFailure() {
    yield put(setCartResumeDone(false));
    yield put(loadingAction({ isLoading: false }));
}

function* processGetCartStart() {
    yield put(loadingAction({ isLoading: true }));
}

function* getCartDataRequestWatcher() {
    yield takeLatest(PROCESS_GET_CART_DATA, processGetCartStart);
    yield takeLatest(RETRIEVED_GET_CART_SUCCESS, processGetCartSuccess);
    yield takeLatest(GET_CART_FAILED, processGetCartFailure);
}

export function* productCartSagasWatcher() {
    yield takeEvery(SAVE_NEW_ITEM_TO_CART, saveNewItemToCart);
    yield takeEvery(RUN_AMOUNT_CALCULATIONS, runAmountCalculations);
    yield call(getCartDataRequestWatcher);
}
